import Hypher from 'hypher';
import hyphenationEnUs from 'hyphenation.en-us';
import { apiFetch } from './apiClient';

const MIN_CHORD_GAP_CHARS = 2;
const NEAR_DUP_MS = 400;
const DEFAULT_PRE_WORD_SNAP_SEC = 0.125; // 1/16 note at 120 BPM

const ENGLISH_HYPHENATOR = new Hypher(hyphenationEnUs);

// ---------------------------------------------------------------------------
// Server-supplied musical song-segment cache.
// Populated by calling fetchPhonemeGraphemes() with lyric words.
// Keys are normalized lowercase words; values are song_segments arrays
// (musically grouped drop-point chunks, e.g. ['a','maz','ing']).
// ---------------------------------------------------------------------------
const _phonemeCache = new Map();

/**
 * Fetch musical song segments for `words` from the server and store them in
 * the module cache.  Uses the /segment-song endpoint which returns grapheme
 * boundary units grouped into musically intuitive chord drop-point chunks
 * (e.g. "amazing" → ['a','maz','ing']).  Safe to call whenever lyrics change;
 * results are merged (not replaced) so existing entries are preserved.
 */
export async function fetchPhonemeGraphemes(words = []) {
  if (!words.length) return;
  const unique = [...new Set(words.map(String).filter(Boolean))];
  if (!unique.length) return;
  try {
    const res = await apiFetch('/segment-song', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ words: unique }),
    });
    if (!res.ok) return;
    const data = await res.json();
    for (const [raw, entry] of Object.entries(data)) {
      // Normalise key to match phonemePlanForWord lookup (lowercase, strip punctuation).
      const key = raw.toLowerCase().replace(/^[^a-z']+|[^a-z']+$/g, '');
      if (!key) continue;
      const segs = Array.isArray(entry?.song_segments) ? entry.song_segments : [];
      const graphemes = segs.map(g => g.toLowerCase()).filter(Boolean);
      if (graphemes.length >= 2) _phonemeCache.set(key, graphemes);
    }
  } catch {
    // Non-critical — fall back to client-side syllabification
  }
}

// Client-side fallback overrides for words not covered by the server dictionary
// or for offline use.  Server /segment-song is the authoritative source for
// common words; entries here are only reached when the server is unavailable
// or returns no result.
const CMU_PHONEME_LEXICON = {
  beautiful: { graphemes: ['beau', 'ti', 'ful'] },
};

const PHONEME_CLASS_WEIGHTS = {
  short_vowel: 1.0,
  long_vowel: 2.2,
  stop: 0.6,
  fricative: 1.5,
  nasal: 1.2,
  liquid_glide: 1.2,
  default: 1.0,
};

const posKey = (wordIndex, syllableOffset = 0) => `${wordIndex}:${syllableOffset}`;

function parseLineWords(line) {
  const words = [];
  const re = /\S+/g;
  let m;
  while ((m = re.exec(line || '')) !== null) {
    words.push({
      wordIndex: words.length,
      start: m.index,
      end: m.index + m[0].length,
    });
  }
  return words;
}

function chordHash(value) {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

export function tokenizeLyricLine(line, options = {}) {
  const splitByWordIndex = options?.splitByWordIndex || {};
  const splitSeparator = options?.splitSeparator || '-';
  const splitSeparatorByWordIndex = options?.splitSeparatorByWordIndex || {};
  const parts = (line || '').split(/(\s+)/);
  let wordIdx = 0;
  const tokens = [];
  let col = 0;

  for (const part of parts) {
    const isWord = /\S/.test(part);
    if (!isWord) {
      tokens.push({
        text: part,
        isWord: false,
        wordIndex: null,
        ownerWordIndex: null,
        syllableOffset: null,
        startCol: col,
        endCol: col + part.length,
      });
      col += part.length;
      continue;
    }

    const hasLetters = /[a-zA-Z]/.test(part);
    if (!hasLetters) {
      tokens.push({
        text: part,
        isWord: false,
        wordIndex: null,
        ownerWordIndex: null,
        syllableOffset: null,
        startCol: col,
        endCol: col + part.length,
      });
      col += part.length;
      continue;
    }

    let segments = [part];

    if (part.includes('-')) {
      segments = part.split(/(?<=-)(?=[^-])/);
    }
    // Optional explicit split hints for mid-word chord changes.
    if (Number.isInteger(wordIdx) && Array.isArray(splitByWordIndex[wordIdx]) && splitByWordIndex[wordIdx].length) {
      const base = part;
      const cuts = [...new Set(splitByWordIndex[wordIdx]
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0 && n < base.length))]
        .sort((a, b) => a - b);
      if (cuts.length) {
        const out = [];
        let cursor = 0;
        cuts.forEach((cut) => {
          const slice = base.slice(cursor, cut);
          if (slice) out.push(slice);
          cursor = cut;
        });
        const tail = base.slice(cursor);
        if (tail) out.push(tail);
        if (out.length) segments = out;
      }
    }

    const currentWordIdx = wordIdx;
    for (let si = 0; si < segments.length; si++) {
      const seg = segments[si];
      tokens.push({
        text: seg,
        isWord: true,
        wordIndex: currentWordIdx,
        ownerWordIndex: currentWordIdx,
        syllableOffset: si,
        startCol: col,
        endCol: col + seg.length,
      });
      col += seg.length;
      if (si < segments.length - 1) {
        const sep = Object.prototype.hasOwnProperty.call(splitSeparatorByWordIndex, currentWordIdx)
          ? String(splitSeparatorByWordIndex[currentWordIdx] ?? '')
          : splitSeparator;
        tokens.push({
          text: sep,
          isWord: false,
          wordIndex: null,
          ownerWordIndex: currentWordIdx,
          syllableOffset: null,
          startCol: col,
          endCol: col + sep.length,
        });
        col += sep.length;
      }
    }
    wordIdx++;
  }

  return tokens;
}

/**
 * Split a raw token text (e.g. a lyric word or sub-word token) into the same
 * phoneme-based grapheme segments that the chord sheet uses for drop zones.
 * Uses the same CMU lexicon and vowel-group splitter as layoutLineV2 so
 * drag-hover granularity exactly matches the rendered split tokens.
 *
 * Returns [rawTokenText] (single element) for single-syllable words.
 */
export function getWordDropSegments(rawTokenText) {
  const raw = String(rawTokenText || '');
  if (raw.length < 2) return [raw];
  const plan = phonemePlanForWord(raw);
  if (!plan || !Array.isArray(plan.graphemes) || plan.graphemes.length < 2) return [raw];
  // Map grapheme lengths back onto the original-case token text.
  // Any trailing punctuation that normalizeSungWord stripped is appended to the last segment.
  const result = [];
  let cursor = 0;
  for (const grapheme of plan.graphemes) {
    const glen = String(grapheme || '').length;
    const chunk = raw.slice(cursor, cursor + glen);
    if (chunk) result.push(chunk);
    cursor += glen;
  }
  const tail = raw.slice(cursor);
  if (tail) {
    if (result.length) result[result.length - 1] += tail;
    else result.push(tail);
  }
  return result.length >= 2 ? result : [raw];
}

export function buildAlignedEvents(alignment = [], lineIndex = null) {
  return (alignment || [])
    .filter((a) => a && a.chord)
    .filter((a) => lineIndex == null || a.lineIndex === lineIndex)
    .map((a) => {
      const wi = Number(a.wordIndex);
      const so = Number(a.syllableOffset || 0);
      const spacer = Boolean(a.isSpacer) || !Number.isInteger(wi);
      const anchorType = spacer ? 'spacer' : (so > 0 ? 'subword' : 'word');
      return {
        lineIndex: a.lineIndex,
        wordIndex: wi,
        syllableOffset: so,
        time: a.time,
        chord: a.chord,
        isSpacer: spacer,
        anchorType,
        anchorRule: a.anchorRule || null,
        anchorMethod: a.anchorMethod || null,
        anchorProgress: Number.isFinite(Number(a.anchorProgress)) ? Number(a.anchorProgress) : null,
        phonemeSpan: Number.isFinite(Number(a.phonemeSpan)) ? Number(a.phonemeSpan) : null,
        gapProgress: Number.isFinite(Number(a.gapProgress)) ? Number(a.gapProgress) : null,
        gapDurationSec: Number.isFinite(Number(a.gapDurationSec)) ? Number(a.gapDurationSec) : null,
      };
    })
    .sort((a, b) => {
      const ta = a.time ?? Number.POSITIVE_INFINITY;
      const tb = b.time ?? Number.POSITIVE_INFINITY;
      if (ta !== tb) return ta - tb;
      if (a.wordIndex !== b.wordIndex) return a.wordIndex - b.wordIndex;
      return (a.syllableOffset || 0) - (b.syllableOffset || 0);
    });
}

function targetColumnForEvent(evt, lineLen, tokenMap, words, lineTokens = null) {
  const hasProgress = Number.isFinite(Number(evt.anchorProgress));
  const wordIndex = Number(evt.wordIndex);
  const syllableOffset = Number(evt.syllableOffset || 0);
  const isVisibleSplitSubword = Boolean(evt.visibleWordSplit) && syllableOffset > 0;

  // For visibly split words, resolve subword anchors directly from split tokens.
  // This avoids drifting back to whole-word interpolation and keeps anchors in the
  // intended interior gap (e.g. "h - o - me" => between "o" and "-").
  if (isVisibleSplitSubword && Array.isArray(lineTokens)) {
    const wordSegments = lineTokens
      .filter((t) => t && t.isWord && Number(t.wordIndex) === wordIndex)
      .sort((a, b) => a.startCol - b.startCol);
    const segIdx = Math.max(1, Math.min(wordSegments.length - 1, syllableOffset));
    const seg = wordSegments[segIdx];
    if (seg) {
      const boundary = seg.endCol;
      const owningSep = lineTokens.find((t) => (
        t
        && !t.isWord
        && Number(t.ownerWordIndex) === wordIndex
        && boundary >= t.startCol
        && boundary <= t.endCol
      ));
      if (owningSep) return owningSep.startCol;
      return boundary;
    }
  }

  const explicitToken = tokenMap[posKey(evt.wordIndex, evt.syllableOffset)];
  if (explicitToken) {
    // For visibly split words (e.g. "h - o - me"), place subword chords on the
    // segment boundary so they can sit between grapheme chunks.
    if (isVisibleSplitSubword) {
      return explicitToken.endCol;
    }
    // If we have a proportional in-word anchor, prefer that over token-start snapping.
    // This allows placement inside sustained vowels / between split segments.
    if (!hasProgress) return explicitToken.startCol;
  }

  if (!Number.isFinite(evt.wordIndex)) return 0;
  if (evt.wordIndex < 0) return 0;

  if (Number.isInteger(evt.wordIndex)) {
    const wi = evt.wordIndex;
    const first = words.find((w) => w.wordIndex === wi);
    if (first) {
      const wordLen = Math.max(1, first.end - first.start);
      const total = Number.isFinite(Number(evt.inWordTotal)) ? Number(evt.inWordTotal) : null;
      const ordinal = Number.isFinite(Number(evt.inWordOrdinal)) ? Number(evt.inWordOrdinal) : null;
      const wantsSubword = Number(evt.syllableOffset || 0) > 0 || (total != null && total > 1) || hasProgress;
      if (wantsSubword) {
        const frac = hasProgress
          ? Math.max(0, Math.min(1, Number(evt.anchorProgress)))
          : (() => {
              const denom = Math.max(1, (total != null ? total - 1 : Number(evt.syllableOffset || 0)));
              const ord = Math.max(0, Math.min(denom, ordinal != null ? ordinal : Number(evt.syllableOffset || 0)));
              return denom > 0 ? (ord / denom) : 0;
            })();
        return first.start + Math.round(frac * Math.max(0, wordLen - 1));
      }
      return first.start;
    }
  }

  // Fractional anchors: interpolate in the nearest inter-word gap.
  if (!words.length) return 0;
  if (evt.wordIndex >= words[words.length - 1].wordIndex) return lineLen;

  const floorWi = Math.floor(evt.wordIndex);
  const before = words.find((w) => w.wordIndex === floorWi);
  const after = words.find((w) => w.wordIndex === floorWi + 1);
  if (!before && !after) return 0;
  const local = Number.isFinite(Number(evt.wordIndex - floorWi))
    ? Math.max(0, Math.min(1, evt.wordIndex - floorWi))
    : 0;
  const gapLocal = Number.isFinite(Number(evt.gapProgress))
    ? Math.max(0, Math.min(1, Number(evt.gapProgress)))
    : local;

  let gapStart = 0;
  let gapEnd = lineLen;
  if (before && after) {
    gapStart = before.end;
    gapEnd = Math.max(gapStart, after.start - 1);
  } else if (!before && after) {
    // Pre-first-word gap: include the leading canonical spacer column(s).
    gapStart = 0;
    gapEnd = Math.max(gapStart, after.start - 1);
  } else if (before && !after) {
    // Post-last-word gap to end-of-line.
    gapStart = before.end;
    gapEnd = lineLen;
  }
  if (gapEnd <= gapStart) return gapStart;
  return Math.round(gapStart + gapLocal * (gapEnd - gapStart));
}

export function layoutLineV2(lyricLine, alignedEvents = []) {
  const sourceLine = lyricLine || '';
  const sourceWords = parseLineWords(sourceLine);

  const eventsBase = buildAlignedEvents(alignedEvents);
  const perWordTimed = {};
  eventsBase.forEach((evt) => {
    if (!Number.isInteger(evt.wordIndex) || evt.wordIndex < 0 || evt.isSpacer) return;
    const k = String(evt.wordIndex);
    if (!perWordTimed[k]) perWordTimed[k] = [];
    perWordTimed[k].push(evt);
  });
  Object.values(perWordTimed).forEach((arr) => {
    arr.sort((a, b) => {
      const ta = a.time ?? Number.POSITIVE_INFINITY;
      const tb = b.time ?? Number.POSITIVE_INFINITY;
      if (ta !== tb) return ta - tb;
      return (a.syllableOffset || 0) - (b.syllableOffset || 0);
    });
    arr.forEach((evt, idx) => {
      evt.inWordOrdinal = idx;
      evt.inWordTotal = arr.length;
    });
  });

  // Candidate internal split points used for subword anchoring.
  // Visibility is decided in a second pass only when needed for readability.
  const splitByWordIndex = {};
  const multiEventSplitWords = new Set();
  Object.entries(perWordTimed).forEach(([k, arr]) => {
    const wi = Number(k);
    const word = sourceWords.find((w) => w.wordIndex === wi);
    if (!word) return;
    const rawText = sourceLine.slice(word.start, word.end);
    if (!rawText || rawText.length < 3) return;
    if (arr.length > 1) multiEventSplitWords.add(wi);
    const cuts = new Set();
    const plan = phonemePlanForWord(rawText);
    // Always prefer dictionary-backed grapheme boundaries so all multi-syllable
    // words expose sub-word drop zones, even when only one chord is placed on them.
    if (Array.isArray(plan?.graphemes) && plan.graphemes.length > 1) {
      let cursor = 0;
      plan.graphemes.slice(0, -1).forEach((seg) => {
        cursor += String(seg || '').length;
        if (cursor > 0 && cursor < rawText.length) cuts.add(cursor);
      });
    } else if (arr.length > 1) {
      // Fallback arc-progress only makes sense when multiple chord events need
      // to be distributed across a single word.
      arr.forEach((evt, idx) => {
        const total = Math.max(1, arr.length - 1);
        const p = Number.isFinite(Number(evt.anchorProgress))
          ? Number(evt.anchorProgress)
          : (idx / total);
        const cut = Math.round(Math.max(1, Math.min(rawText.length - 1, p * Math.max(1, rawText.length - 1))));
        if (cut > 0 && cut < rawText.length) cuts.add(cut);
      });
    }
    const sortedCuts = [...cuts].sort((a, b) => a - b);
    if (sortedCuts.length) {
      splitByWordIndex[wi] = sortedCuts;
    }
  });
  const renderWithVisibleSplits = (visibleSplitWords = new Set()) => {
    const splitSeparatorByWordIndex = {};
    Object.keys(splitByWordIndex).forEach((k) => {
      const wi = Number(k);
      splitSeparatorByWordIndex[wi] = visibleSplitWords.has(wi) ? ' - ' : '';
    });

    let tokens = tokenizeLyricLine(sourceLine, {
      splitByWordIndex,
      splitSeparator: ' - ',
      splitSeparatorByWordIndex,
    });

    const computeWordSpans = (toks) => {
      const ws = [];
      toks.forEach((t) => {
        if (!t.isWord) return;
        const existing = ws.find((w) => w.wordIndex === t.wordIndex);
        if (!existing) {
          ws.push({ wordIndex: t.wordIndex, start: t.startCol, end: t.endCol });
          return;
        }
        existing.end = Math.max(existing.end, t.endCol);
      });
      ws.sort((a, b) => a.wordIndex - b.wordIndex);
      return ws;
    };

    // Keep a permanent leading spacer on every lyric line so line-start drop
    // targets are stable and have enough room for short chord labels.
    const MIN_LEADING_SPACES = 1;
    const firstTok = tokens[0];
    const firstText = String(firstTok?.text || '');
    const leadingSpaceCount = (!firstTok || firstTok.isWord || firstText.trim() !== '')
      ? 0
      : firstText.length;
    if (leadingSpaceCount < MIN_LEADING_SPACES) {
      const pad = ' '.repeat(MIN_LEADING_SPACES - leadingSpaceCount);
      let col = 0;
      const next = [];
      if (pad.length) {
        next.push({
          text: pad,
          isWord: false,
          wordIndex: null,
          ownerWordIndex: null,
          syllableOffset: null,
          startCol: 0,
          endCol: pad.length,
        });
        col = pad.length;
      }
      tokens.forEach((t, idx) => {
        if (idx === 0 && leadingSpaceCount > 0) {
          const mergedText = `${firstText}`;
          const out = { ...t, text: mergedText, startCol: col, endCol: col + mergedText.length };
          next.push(out);
          col = out.endCol;
          return;
        }
        const out = { ...t, startCol: col, endCol: col + String(t.text || '').length };
        next.push(out);
        col = out.endCol;
      });
      tokens = next;
    }
    // Keep a permanent trailing spacer for stable post-edge drop target.
    const MIN_TRAILING_SPACES = 1;
    const lastTok = tokens[tokens.length - 1];
    const lastText = String(lastTok?.text || '');
    const trailingSpaceCount = (!lastTok || lastTok.isWord || lastText.trim() !== '')
      ? 0
      : lastText.length;
    if (trailingSpaceCount < MIN_TRAILING_SPACES) {
      const pad = ' '.repeat(MIN_TRAILING_SPACES - trailingSpaceCount);
      if (pad.length) {
        if (lastTok && !lastTok.isWord && lastText.trim() === '') {
          const merged = `${lastText}${pad}`;
          const startCol = Number(lastTok.startCol || 0);
          tokens[tokens.length - 1] = {
            ...lastTok,
            text: merged,
            startCol,
            endCol: startCol + merged.length,
          };
        } else {
          const startCol = tokens.length ? Number(tokens[tokens.length - 1].endCol || 0) : 0;
          tokens.push({
            text: pad,
            isWord: false,
            wordIndex: null,
            ownerWordIndex: null,
            syllableOffset: null,
            startCol,
            endCol: startCol + pad.length,
          });
        }
      }
    }
    const tokenMap = {};
    tokens.forEach((t) => {
      if (t.isWord) tokenMap[posKey(t.wordIndex, t.syllableOffset || 0)] = t;
    });
    const words = computeWordSpans(tokens);
    const lyricLineText = tokens.map((t) => t.text).join('');
    const lyricLen = lyricLineText.length;
    const splitSeparatorsByWord = {};
    tokens.forEach((t) => {
      if (t.isWord || !Number.isInteger(t.ownerWordIndex)) return;
      if (!String(t.text || '').includes('-')) return;
      const wi = Number(t.ownerWordIndex);
      if (!splitSeparatorsByWord[wi]) splitSeparatorsByWord[wi] = [];
      splitSeparatorsByWord[wi].push(t);
    });
    Object.values(splitSeparatorsByWord).forEach((arr) => arr.sort((a, b) => a.startCol - b.startCol));

    const resolveVisibleSplitGapTarget = (evt, baseTargetCol) => {
      if (!evt.visibleWordSplit || evt.anchorType !== 'subword' || !Number.isInteger(evt.wordIndex)) {
        return baseTargetCol;
      }
      const seps = splitSeparatorsByWord[Number(evt.wordIndex)] || [];
      if (!seps.length) return baseTargetCol;
      const p = Number(evt.anchorProgress);
      let sepIdx;
      if (Number.isFinite(p)) {
        sepIdx = Math.max(0, Math.min(seps.length - 1, Math.floor(Math.max(0, Math.min(0.999, p)) * seps.length)));
      } else {
        sepIdx = Math.max(0, Math.min(seps.length - 1, Number(evt.syllableOffset || 1) - 1));
      }
      return seps[sepIdx].startCol;
    };

    const events = eventsBase.map((evt) => {
      const visibleWordSplit = !!(Number.isInteger(evt.wordIndex)
        && String(splitSeparatorByWordIndex[Number(evt.wordIndex)] || '').trim());
      const withSplit = { ...evt, visibleWordSplit };
      const baseTarget = targetColumnForEvent(withSplit, lyricLen, tokenMap, words, tokens);
      return {
        ...withSplit,
        targetCol: resolveVisibleSplitGapTarget(withSplit, baseTarget),
      };
    }).sort((a, b) => {
      const ta = a.time ?? Number.POSITIVE_INFINITY;
      const tb = b.time ?? Number.POSITIVE_INFINITY;
      if (ta !== tb) return ta - tb;
      if (a.targetCol !== b.targetCol) return a.targetCol - b.targetCol;
      if (a.wordIndex !== b.wordIndex) return a.wordIndex - b.wordIndex;
      return (a.syllableOffset || 0) - (b.syllableOffset || 0);
    });

    const placements = [];
    const pressureWords = new Set();
    let lastStart = null;
    let lastLen = null;
    let lastEvt = null;
    for (const evt of events) {
      const label = String(evt.chord || '').trim();
      if (!label) continue;
      // Render-boundary dedupe: chord charts represent changes, so adjacent same-label
      // events are collapsed into a single visible chord.
      if (lastEvt && lastEvt.chord === label) {
        continue;
      }
      // Timing contract: chord onset is represented by the LEFT edge of the label.
      // Do not center labels around anchor points (it causes apparent timing drift
      // for longer symbols like "Bbmaj7" vs shorter symbols like "Bb").
      let start = evt.targetCol;
      start = Math.max(0, start);
      if (lastStart != null) {
        const sameWordAsPrev =
          lastEvt
          && Number.isFinite(Number(lastEvt.wordIndex))
          && Number.isFinite(Number(evt.wordIndex))
          && Number(lastEvt.wordIndex) === Number(evt.wordIndex);
        const minGap = sameWordAsPrev ? 1 : MIN_CHORD_GAP_CHARS;
        const minStart = lastStart + lastLen + minGap;
        if (start < minStart) {
          if (sameWordAsPrev && Number.isFinite(Number(evt.wordIndex))) {
            pressureWords.add(Number(evt.wordIndex));
          }
          start = minStart;
        }
      }
      placements.push({ ...evt, chord: label, col: start });
      lastEvt = evt;
      lastStart = start;
      lastLen = label.length;
    }
    return { tokens, tokenMap, lyricLineText, lyricLen, placements, splitSeparatorByWordIndex, pressureWords };
  };

  const firstPass = renderWithVisibleSplits(new Set());
  const visibleSplitWords = new Set([
    ...multiEventSplitWords,
    ...[...firstPass.pressureWords].filter((wi) => multiEventSplitWords.has(wi)),
  ]);
  const finalPass = visibleSplitWords.size > 0 ? renderWithVisibleSplits(visibleSplitWords) : firstPass;
  const { tokens, tokenMap, lyricLineText, lyricLen, placements, splitSeparatorByWordIndex } = finalPass;

  let totalLen = lyricLen;
  for (const p of placements) totalLen = Math.max(totalLen, p.col + p.chord.length);

  const chordChars = Array(totalLen).fill(' ');
  for (const p of placements) {
    for (let i = 0; i < p.chord.length; i++) chordChars[p.col + i] = p.chord[i];
  }

  const chordRow = chordChars.join('').replace(/\s+$/, '');
  const lyricRow = lyricLineText.padEnd(totalLen, ' ').replace(/\s+$/, '');
  const chordPositions = placements.map((p) => ({ chord: p.chord, col: p.col }));

  return {
    chordRow,
    lyricRow,
    chordPositions,
    tokens,
    tokenMap,
    alignedEvents: placements,
    splitDecisions: Object.keys(splitByWordIndex).map((k) => ({
      wordIndex: Number(k),
      cuts: splitByWordIndex[k],
      visibleSeparator: (splitSeparatorByWordIndex[Number(k)] || '') !== '',
    })),
    parityHash: chordHash(`${chordRow}\n${lyricRow}`),
  };
}

export function buildRenderParityHash(lineLayouts = []) {
  return chordHash((lineLayouts || []).map((l) => `${l.chordRow || ''}\n${l.lyricRow || ''}`).join('\n'));
}

export function buildLineLayouts(lyrics = '', alignment = []) {
  const lines = (lyrics || '').split('\n');
  const byLine = {};
  (alignment || []).forEach((evt) => {
    if (evt?.lineIndex == null) return;
    if (!byLine[evt.lineIndex]) byLine[evt.lineIndex] = [];
    byLine[evt.lineIndex].push(evt);
  });
  return lines.map((line, lineIndex) => layoutLineV2(line, byLine[lineIndex] || []));
}

function sectionHeaderTargetIndex(line, lineIndex) {
  const sectionLower = String(line || '').trim().toLowerCase();
  if (/^\[intro/.test(sectionLower)) return -1;
  if (/^\[outro/.test(sectionLower)) return -2;
  return lineIndex;
}

export function buildRenderableSongModel(lyrics = '', alignment = []) {
  const lines = (lyrics || '').split('\n');
  const byLine = {};
  (alignment || []).forEach((evt) => {
    if (evt?.lineIndex == null || !evt?.chord) return;
    if (!byLine[evt.lineIndex]) byLine[evt.lineIndex] = [];
    byLine[evt.lineIndex].push(evt);
  });

  let justCrossedSection = false;
  return lines.map((line, lineIndex) => {
    const trimmed = (line || '').trim();
    if (!trimmed) return { type: 'blank', lineIndex, text: '' };
    if (/^\[.+\]$/.test(trimmed)) {
      justCrossedSection = true;
      const targetIndex = sectionHeaderTargetIndex(trimmed, lineIndex);
      const headerEvents = byLine[targetIndex] || [];
      const headerChordRow = headerEvents.length
        ? headerEvents
            .slice()
            .sort((a, b) => {
              if ((a.wordIndex ?? 0) !== (b.wordIndex ?? 0)) return (a.wordIndex ?? 0) - (b.wordIndex ?? 0);
              return (a.syllableOffset ?? 0) - (b.syllableOffset ?? 0);
            })
            .map((a) => a.chord)
            .join('  ')
        : '';
      return {
        type: 'section',
        lineIndex,
        text: trimmed,
        headerChordRow,
      };
    }
    let lineEvents = (byLine[lineIndex] || []).slice();
    if (justCrossedSection && lineEvents.length > 1) {
      const wordAnchors = lineEvents
        .filter((e) => Number.isInteger(Number(e.wordIndex)))
        .sort((a, b) => {
          if (Number(a.wordIndex) !== Number(b.wordIndex)) return Number(a.wordIndex) - Number(b.wordIndex);
          return Number(a.syllableOffset || 0) - Number(b.syllableOffset || 0);
        });
      const k0 = wordAnchors.find((e) => Number(e.wordIndex) === 0 && Number(e.syllableOffset || 0) === 0);
      const k1 = wordAnchors.find((e) => Number(e.wordIndex) === 1 && Number(e.syllableOffset || 0) === 0);
      if (k0 && k1 && String(k0.chord) === String(k1.chord)) {
        lineEvents = lineEvents.filter((e) => !(e === k0));
      }
    }
    justCrossedSection = false;
    const layout = layoutLineV2(line, lineEvents);
    return {
      type: 'lyric',
      lineIndex,
      text: line,
      chordRow: layout.chordRow,
      lyricRow: layout.lyricRow,
      chordPositions: layout.chordPositions,
      tokens: layout.tokens || [],
      alignedEvents: layout.alignedEvents,
      splitDecisions: layout.splitDecisions || [],
      parityHash: layout.parityHash,
    };
  });
}

function modelRowsForHash(model = []) {
  return (model || []).flatMap((row) => {
    if (row.type === 'blank') return [''];
    if (row.type === 'section') return [row.text || '', row.headerChordRow || ''];
    if (row.type === 'lyric') return [row.chordRow || '', row.lyricRow || row.text || ''];
    return [''];
  });
}

export function buildSurfaceParity(lyrics = '', alignment = []) {
  const model = buildRenderableSongModel(lyrics, alignment);
  const webRows = modelRowsForHash(model);
  const textRows = modelRowsForHash(model);
  const pdfRows = modelRowsForHash(model);
  const web = chordHash(webRows.join('\n'));
  const text = chordHash(textRows.join('\n'));
  const pdf = chordHash(pdfRows.join('\n'));
  const firstMismatchLine = webRows.findIndex((r, idx) => r !== textRows[idx] || r !== pdfRows[idx]);
  return {
    model,
    web,
    text,
    pdf,
    renderParityHash: chordHash(`${web}|${text}|${pdf}`),
    renderParityDiff: firstMismatchLine >= 0
      ? {
          lineIndex: firstMismatchLine,
          web: webRows[firstMismatchLine] || '',
          text: textRows[firstMismatchLine] || '',
          pdf: pdfRows[firstMismatchLine] || '',
        }
      : null,
  };
}

export function buildTimingAudit(alignment = [], timedLyrics = [], toleranceSec = 0.35) {
  const wordsByLine = buildLineWordTimingMap(timedLyrics);
  const lineStats = {};
  const ensureLine = (li) => {
    if (!lineStats[li]) {
      lineStats[li] = {
        totalTimedAnchors: 0,
        wordAnchorOutOfBounds: 0,
        gapAnchorOutOfBounds: 0,
        nonMonotonicTimeCount: 0,
      };
    }
    return lineStats[li];
  };

  const byLine = {};
  (alignment || []).forEach((a) => {
    if (!a || !a.chord || a.lineIndex == null || a.time == null) return;
    const li = Number(a.lineIndex);
    if (!Number.isFinite(li) || li < 0) return;
    if (!byLine[li]) byLine[li] = [];
    byLine[li].push(a);
    ensureLine(li).totalTimedAnchors += 1;
  });

  Object.keys(byLine).map(Number).forEach((li) => {
    const stats = ensureLine(li);
    const words = wordsByLine[li] || [];
    const wordMap = {};
    words.forEach((w) => { wordMap[w.wordIndex] = w; });
    const evts = byLine[li].slice().sort((a, b) => Number(a.time) - Number(b.time));
    for (let i = 1; i < evts.length; i++) {
      if (Number(evts[i].time) + 1e-9 < Number(evts[i - 1].time)) stats.nonMonotonicTimeCount += 1;
    }
    evts.forEach((evt) => {
      const t = Number(evt.time);
      const wi = Number(evt.wordIndex);
      if (!Number.isFinite(wi)) return;
      if (Number.isInteger(wi)) {
        const w = wordMap[wi];
        if (!w) return;
        if (t < w.start - toleranceSec || t > w.end + toleranceSec) stats.wordAnchorOutOfBounds += 1;
        return;
      }
      const floorWi = Math.floor(wi);
      const ceilWi = Math.ceil(wi);
      const left = wordMap[floorWi];
      const right = wordMap[ceilWi];
      if (left && right) {
        if (t < left.end - toleranceSec || t > right.start + toleranceSec) stats.gapAnchorOutOfBounds += 1;
      } else if (left && t < left.end - toleranceSec) {
        stats.gapAnchorOutOfBounds += 1;
      } else if (right && t > right.start + toleranceSec) {
        stats.gapAnchorOutOfBounds += 1;
      }
    });
  });

  const aggregate = Object.values(lineStats).reduce((acc, s) => {
    acc.totalTimedAnchors += s.totalTimedAnchors;
    acc.wordAnchorOutOfBounds += s.wordAnchorOutOfBounds;
    acc.gapAnchorOutOfBounds += s.gapAnchorOutOfBounds;
    acc.nonMonotonicTimeCount += s.nonMonotonicTimeCount;
    return acc;
  }, {
    totalTimedAnchors: 0,
    wordAnchorOutOfBounds: 0,
    gapAnchorOutOfBounds: 0,
    nonMonotonicTimeCount: 0,
  });

  return { aggregate, byLine: lineStats };
}

function buildLineWordTimingMap(timedLyrics = []) {
  const byLine = {};
  (timedLyrics || []).forEach((w) => {
    if (w?.lineIndex == null || w?.wordIndex == null) return;
    if (w.start == null) return;
    const li = Number(w.lineIndex);
    if (!byLine[li]) byLine[li] = [];
    const start = Number(w.start);
    const endValue = Number(w.end);
    const end = Number.isFinite(endValue) ? Math.max(start, endValue) : start;
    byLine[li].push({
      wordIndex: Number(w.wordIndex),
      start,
      end,
      word: w.word || '',
    });
  });
  Object.keys(byLine).forEach((k) => {
    byLine[k].sort((a, b) => a.wordIndex - b.wordIndex);
  });
  return byLine;
}

function buildVowelAnchors(wordText = '') {
  const text = String(wordText || '');
  const anchors = [];
  const re = /[aeiouy]+/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const center = (start + end - 1) / 2;
    const denom = Math.max(1, text.length - 1);
    anchors.push(Math.max(0, Math.min(1, center / denom)));
  }
  return anchors;
}

function normalizeSungWord(word = '') {
  let out = String(word || '').toLowerCase().trim();
  out = out.replace(/^[^a-z'-]+|[^a-z'-]+$/g, '');
  out = out.replace(/'+/g, '');
  // collapse melisma style "ho-o-o-me" -> "hooome"
  out = out.replace(/-/g, '');
  // collapse long repeated vowels for dictionary lookup
  out = out.replace(/([aeiouy])\1{2,}/g, '$1');
  return out;
}

// Recursively split a grapheme segment at phonetically meaningful boundaries.
// Hypher is a typographic hyphenation library (Knuth-Liang) that merges short
// syllables for print aesthetics (e.g. "amaz-ing" rather than "a-maz-ing").
// This function re-splits any segment that still contains two or more distinct
// vowel nuclei, using the syllabic onset principle:
//   - single consonant between vowels → give to next syllable as onset
//   - multi-consonant cluster → split after the first (current syllable keeps 1 coda)
// Special cases:
//   - word-final silent 'e' (e.g. "lieve", "saved") → segment treated as 1 syllable
//   - C+'er'|C+'le' at end of segment (syllabic sonorant) → consonant stays as coda
//     of preceding syllable (e.g. "deemer"→"deem·er", "ever"→"ev·er")
function splitByVowelGroups(seg) {
  const vRe = /[aeiouy]+/gi;
  const groups = [];
  let m;
  while ((m = vRe.exec(seg)) !== null) {
    groups.push({ start: m.index, end: m.index + m[0].length });
  }
  if (groups.length < 2) return [seg];

  // Silent 'e': if exactly 2 vowel groups and the last is a bare 'e' followed by
  // a non-syllabic ending (empty, 'd', 's', 'st', etc.), treat whole as 1 syllable.
  if (groups.length === 2) {
    const lastGroup = groups[1];
    const afterLast = seg.slice(lastGroup.end).toLowerCase();
    const lastVowelStr = seg.slice(lastGroup.start, lastGroup.end).toLowerCase();
    const SILENT_E_ENDINGS = new Set(['', 'd', 's', 'st', 'nd', 'nt', 'ed', 'es', 'ss', 'ts']);
    if (lastVowelStr === 'e' && SILENT_E_ENDINGS.has(afterLast)) return [seg];
  }

  const clusterStart = groups[0].end;
  const clusterEnd = groups[1].start;
  const clusterLen = clusterEnd - clusterStart;
  // Default: single consonant → onset of next syllable; multi → split after first
  let splitAt = clusterLen <= 1 ? clusterStart : clusterStart + 1;

  // C+'er' / C+'le' ending: the consonant before the syllabic sonorant belongs as
  // coda of the current syllable ("deemer"→deem+er, "ever"→ev+er, "apple"→ap+ple).
  const rightPreview = seg.slice(clusterStart);
  if (/^[^aeiouy](er|le)$/i.test(rightPreview)) {
    splitAt = clusterStart + 1;
  }

  const left = seg.slice(0, splitAt);
  const right = seg.slice(splitAt);
  if (!left || !right) return [seg];
  return [...splitByVowelGroups(left), ...splitByVowelGroups(right)];
}

function inferGraphemeBoundaries(normalizedWord = '') {
  const raw = String(normalizedWord || '');
  if (raw.length < 3) return null;

  const byHyphenation = ENGLISH_HYPHENATOR.hyphenate(raw).filter(Boolean);
  if (byHyphenation.length >= 2 && byHyphenation.join('') === raw) {
    // Hypher gives typographic breaks; post-process to split any segment that
    // still contains multiple vowel nuclei (phonetic syllable boundaries).
    const refined = byHyphenation.flatMap((seg) => splitByVowelGroups(seg));
    return refined.length >= 2 ? refined : byHyphenation;
  }

  // Fallback for words Hypher treats as a single segment (typically 1-syllable words).
  // Apply vowel-group splitting directly so that single-syllable words like "grace",
  // "sweet", "sound" correctly return null (silent-e / single vowel nucleus), while
  // genuine multi-syllable words Hypher missed (e.g. "holy") still split correctly.
  // The old onset/nucleus/coda regex split 1-syllable words into 3 sub-syllabic
  // micro-segments ("gr","a","ce") which created unwanted character-level drop zones.
  const fallbackSegs = splitByVowelGroups(raw);
  return fallbackSegs.length >= 2 ? fallbackSegs : null;
}

function classifyPhoneme(base) {
  const p = String(base || '').toUpperCase().replace(/[0-9]/g, '');
  if (!p) return 'default';
  const shortVowels = new Set(['IH', 'EH', 'AE', 'AH', 'UH']);
  const longVowels = new Set(['IY', 'EY', 'AY', 'OW', 'UW', 'AO', 'ER', 'OY', 'AW', 'AA']);
  const stops = new Set(['P', 'B', 'T', 'D', 'K', 'G']);
  const fricatives = new Set(['F', 'V', 'TH', 'DH', 'S', 'Z', 'SH', 'ZH', 'HH', 'CH', 'JH']);
  const nasals = new Set(['M', 'N', 'NG']);
  const liquids = new Set(['L', 'R', 'W', 'Y']);
  if (shortVowels.has(p)) return 'short_vowel';
  if (longVowels.has(p)) return 'long_vowel';
  if (stops.has(p)) return 'stop';
  if (fricatives.has(p)) return 'fricative';
  if (nasals.has(p)) return 'nasal';
  if (liquids.has(p)) return 'liquid_glide';
  return 'default';
}

function phonemePlanForWord(rawWord = '') {
  const normalized = normalizeSungWord(rawWord);
  if (!normalized || normalized.length < 2) return null;

  // 1. Server-supplied phoneme cache (highest priority, most accurate).
  if (_phonemeCache.has(normalized)) {
    const graphemes = _phonemeCache.get(normalized);
    if (graphemes.length >= 2) {
      return { word: normalized, phonemes: [], graphemes, anchors: [] };
    }
  }

  // 2. Manual lexicon overrides for irregular sung forms.
  const entry = CMU_PHONEME_LEXICON[normalized];
  const phones = Array.isArray(entry) ? entry : entry?.phonemes;
  const graphemes = Array.isArray(entry?.graphemes) && entry.graphemes.length
    ? entry.graphemes
    : inferGraphemeBoundaries(normalized);
  if (!phones || !phones.length) {
    if (!graphemes || graphemes.length < 2) return null;
    // No phoneme dictionary hit: still return grapheme plan so split boundaries
    // are stable across occurrences of the same word.
    return {
      word: normalized,
      phonemes: [],
      graphemes,
      anchors: [],
    };
  }
  if (!phones || !phones.length) return null;
  const weights = phones.map((ph) => PHONEME_CLASS_WEIGHTS[classifyPhoneme(ph)] ?? PHONEME_CLASS_WEIGHTS.default);
  const total = weights.reduce((s, w) => s + w, 0);
  if (!total || !Number.isFinite(total)) return null;
  let c = 0;
  const anchors = weights.map((w) => {
    const center = c + (w / 2);
    c += w;
    return center / total;
  });
  return {
    word: normalized,
    phonemes: phones,
    graphemes,
    anchors,
  };
}

function wordTokensForIndex(lineTokens, wordIndex) {
  return (lineTokens || []).filter((t) => t.isWord && t.wordIndex === wordIndex);
}

function pickSubwordOffset(tokens, progress01, options = {}) {
  if (!tokens || !tokens.length) return { offset: 0, method: 'linear', progressUsed: Math.max(0, Math.min(1, progress01)) };
  if (tokens.length <= 1 && Array.isArray(options.preferredAnchors) && options.preferredAnchors.length) {
    const p = Math.max(0, Math.min(1, progress01));
    let best = options.preferredAnchors[0];
    let bestIdx = 0;
    let bestDist = Math.abs(best - p);
    for (let i = 1; i < options.preferredAnchors.length; i++) {
      const d = Math.abs(options.preferredAnchors[i] - p);
      if (d < bestDist) {
        best = options.preferredAnchors[i];
        bestIdx = i;
        bestDist = d;
      }
    }
    return {
      offset: Math.max(0, bestIdx),
      method: options.preferredMethod || 'phoneme',
      progressUsed: best,
      phonemeSpan: bestIdx,
    };
  }
  if (tokens.length <= 1) {
    const p = Math.max(0, Math.min(1, progress01));
    // Deterministic fallback for unsplit words without dictionary anchors:
    // if the chord is clearly mid-word by timing, treat it as an internal anchor.
    const midWord = p > 0.18 && p < 0.82;
    return {
      offset: midWord ? 1 : 0,
      method: 'linear',
      progressUsed: p,
    };
  }
  const wordText = tokens.map((t) => t.text).join('');
  const preferredAnchors = (options.preferredAnchors || []).filter((a) => Number.isFinite(a));
  const vowelAnchors = buildVowelAnchors(wordText);
  const p = Math.max(0, Math.min(1, progress01));

  let chosenP = p;
  let method = 'linear';
  let phonemeSpan = null;
  if (preferredAnchors.length) {
    let best = preferredAnchors[0];
    let bestIdx = 0;
    let bestDist = Math.abs(best - p);
    for (let i = 1; i < preferredAnchors.length; i++) {
      const d = Math.abs(preferredAnchors[i] - p);
      if (d < bestDist) {
        best = preferredAnchors[i];
        bestIdx = i;
        bestDist = d;
      }
    }
    if (bestDist <= 0.35) {
      chosenP = best;
      phonemeSpan = bestIdx;
      method = options.preferredMethod || 'phoneme';
    }
  }
  if (vowelAnchors.length) {
    let nearest = vowelAnchors[0];
    let nearestDist = Math.abs(vowelAnchors[0] - p);
    for (let i = 1; i < vowelAnchors.length; i++) {
      const d = Math.abs(vowelAnchors[i] - p);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = vowelAnchors[i];
      }
    }
    if (nearestDist <= 0.22) {
      chosenP = nearest;
      method = 'vowel';
    }
  }

  const totalChars = Math.max(1, wordText.length);
  const targetChar = Math.round(chosenP * Math.max(1, totalChars - 1));
  let consumed = 0;
  for (let si = 0; si < tokens.length; si++) {
    consumed += tokens[si].text.length;
    if (targetChar < consumed) return { offset: si, method, progressUsed: chosenP, phonemeSpan };
  }
  return { offset: tokens.length - 1, method, progressUsed: chosenP, phonemeSpan };
}

function anchorEventByTime(evt, lineWords, lineTokens, preWordSnapSec = DEFAULT_PRE_WORD_SNAP_SEC) {
  const t = Number(evt.time);
  if (!Number.isFinite(t) || !lineWords?.length) {
    return { ...evt };
  }

  const first = lineWords[0];
  const last = lineWords[lineWords.length - 1];
  if (t < first.start) {
    return { ...evt, wordIndex: first.wordIndex - 0.5, syllableOffset: 0, isSpacer: true, anchorType: 'spacer', anchorRule: 'pre-word' };
  }
  if (t > last.end) {
    return { ...evt, wordIndex: last.wordIndex + 0.5, syllableOffset: 0, isSpacer: true, anchorType: 'spacer', anchorRule: 'post-word' };
  }

  for (let i = 0; i < lineWords.length; i++) {
    const w = lineWords[i];
    if (t >= w.start && t <= w.end) {
      const dur = Math.max(0.05, w.end - w.start);
      const p = (t - w.start) / dur;
      const tokens = wordTokensForIndex(lineTokens, w.wordIndex);
      const plan = phonemePlanForWord(w.word || tokens.map((x) => x.text).join(''));
      const { offset: so, method, progressUsed, phonemeSpan } = pickSubwordOffset(tokens, p, {
        preferredAnchors: plan?.anchors || [],
        preferredMethod: 'phoneme',
      });
      // If chord onset is within 1/16-note AFTER the word start, snap to word start.
      const startSnapFrac = Math.min(0.49, Math.max(0, preWordSnapSec / dur));
      const snappedToWordStart = p <= startSnapFrac;
      const minInsideOffset = snappedToWordStart
        ? 0
        : ((p > 0.001 && p < 0.999) ? Math.max(1, so) : so);
      return {
        ...evt,
        wordIndex: w.wordIndex,
        syllableOffset: minInsideOffset,
        isSpacer: false,
        anchorType: minInsideOffset > 0 ? 'subword' : 'word',
        anchorRule: snappedToWordStart
          ? 'near-word-start-snap'
          : (method === 'phoneme'
          ? 'in-word-phoneme'
          : (method === 'vowel' ? 'in-word-vowel' : 'in-word-linear')),
        anchorMethod: snappedToWordStart ? 'linear' : method,
        anchorProgress: Number((snappedToWordStart ? 0 : (progressUsed ?? p)).toFixed(3)),
        phonemeSpan: Number.isInteger(phonemeSpan) ? phonemeSpan : null,
      };
    }
    const next = lineWords[i + 1];
    if (next && t > w.end && t < next.start) {
      const gapDur = Math.max(0.001, next.start - w.end);
      const gapProg = Math.max(0, Math.min(1, (t - w.end) / gapDur));
      // Only allow pre-word snapping when chord change is within one 1/16 note
      // before the next word start. Otherwise keep it in the gap.
      if ((next.start - t) <= preWordSnapSec) {
        return {
          ...evt,
          wordIndex: next.wordIndex,
          syllableOffset: 0,
          isSpacer: false,
          anchorType: 'word',
          anchorRule: 'near-word-start-snap',
          anchorMethod: 'linear',
          anchorProgress: 0,
          phonemeSpan: null,
          gapProgress: null,
          gapDurationSec: Number(gapDur.toFixed(3)),
        };
      }
      return {
        ...evt,
        wordIndex: w.wordIndex + 0.5,
        syllableOffset: 0,
        isSpacer: true,
        anchorType: 'spacer',
        anchorRule: 'between-words',
        gapProgress: Number(gapProg.toFixed(3)),
        gapDurationSec: Number(gapDur.toFixed(3)),
      };
    }
  }

  return { ...evt };
}

export function normalizeAlignmentTimeFirst({
  lyrics = '',
  alignment = [],
  timedLyrics = [],
  bpm = null,
  duplicateWindowMs = NEAR_DUP_MS,
} = {}) {
  const lines = (lyrics || '').split('\n');
  const lineTokens = {};
  lines.forEach((line, li) => { lineTokens[li] = tokenizeLyricLine(line); });
  const wordsByLine = buildLineWordTimingMap(timedLyrics);

  const grouped = {};
  (alignment || []).forEach((a, idx) => {
    if (!a || a.chord == null || a.lineIndex == null || Number.isNaN(Number(a.lineIndex))) return;
    const li = Number(a.lineIndex);
    if (!grouped[li]) grouped[li] = [];
    grouped[li].push({ ...a, _idx: idx });
  });

  const diagnostics = {
    reorderedByTimeCount: 0,
    collapsedNearDuplicateCount: 0,
    collapsedExactTimeDuplicateCount: 0,
    perLineAnchorPlacements: {},
  };

  const out = [];
  Object.keys(grouped).map(Number).sort((a, b) => a - b).forEach((li) => {
    const events = grouped[li];
    if (li < 0) {
      events
        .sort((a, b) => {
          const ta = a.time ?? Number.POSITIVE_INFINITY;
          const tb = b.time ?? Number.POSITIVE_INFINITY;
          if (ta !== tb) return ta - tb;
          if ((a.wordIndex ?? 0) !== (b.wordIndex ?? 0)) return (a.wordIndex ?? 0) - (b.wordIndex ?? 0);
          return (a.syllableOffset ?? 0) - (b.syllableOffset ?? 0);
        })
        .forEach((e) => out.push({ ...e }));
      return;
    }

    const originalOrder = events.map((e) => e._idx).join(',');
    const timed = events.filter((e) => e.time != null).sort((a, b) => {
      const ta = Number(a.time);
      const tb = Number(b.time);
      if (ta !== tb) return ta - tb;
      return a._idx - b._idx;
    });
    const untimed = events.filter((e) => e.time == null).sort((a, b) => {
      if ((a.wordIndex ?? 0) !== (b.wordIndex ?? 0)) return (a.wordIndex ?? 0) - (b.wordIndex ?? 0);
      return (a.syllableOffset ?? 0) - (b.syllableOffset ?? 0);
    });
    const sortedOrder = [...timed, ...untimed].map((e) => e._idx).join(',');
    if (sortedOrder !== originalOrder) diagnostics.reorderedByTimeCount += 1;

    const collapsed = [];
    for (let i = 0; i < timed.length; i++) {
      const cur = timed[i];
      const prev = collapsed[collapsed.length - 1];
      if (prev && prev.chord === cur.chord) {
        const dtMs = Math.abs((Number(cur.time) - Number(prev.time)) * 1000);
        if (dtMs <= duplicateWindowMs) {
          diagnostics.collapsedNearDuplicateCount += 1;
          continue;
        }
      }
      collapsed.push(cur);
    }

    const preWordSnapSec = (Number.isFinite(Number(bpm)) && Number(bpm) > 0)
      ? (60 / Number(bpm) / 4)
      : DEFAULT_PRE_WORD_SNAP_SEC;
    const normalizedTimed = collapsed.map((evt) => {
      // Preserve explicit manual drop anchors exactly as chosen by the user.
      // Re-anchoring these by timing causes visible drift (often centered).
      if (String(evt.anchorRule || '').startsWith('manual-slot-drop')) {
        const wi = Number(evt.wordIndex);
        const so = Number(evt.syllableOffset || 0);
        const spacer = Boolean(evt.isSpacer) || !Number.isInteger(wi);
        return {
          ...evt,
          isSpacer: spacer,
          anchorType: spacer ? 'spacer' : (so > 0 ? 'subword' : 'word'),
        };
      }
      return anchorEventByTime(
        evt,
        wordsByLine[li] || [],
        lineTokens[li] || [],
        preWordSnapSec,
      );
    });
    // Ensure unique anchors for multiple timed events mapped to the same word.
    // This prevents overwrite collisions in position-keyed maps.
    const byWord = {};
    normalizedTimed.forEach((evt, idx) => {
      if (evt.isSpacer || !Number.isInteger(evt.wordIndex)) return;
      const k = String(evt.wordIndex);
      if (!byWord[k]) byWord[k] = [];
      byWord[k].push({ evt, idx });
    });
    Object.values(byWord).forEach((group) => {
      if (group.length <= 1) return;
      group.sort((a, b) => {
        const ta = a.evt.time ?? Number.POSITIVE_INFINITY;
        const tb = b.evt.time ?? Number.POSITIVE_INFINITY;
        if (ta !== tb) return ta - tb;
        return a.idx - b.idx;
      });
      let prevOffset = -1;
      group.forEach((g, localIdx) => {
        const baseOffset = Number.isFinite(Number(g.evt.syllableOffset)) ? Number(g.evt.syllableOffset) : localIdx;
        const nextOffset = Math.max(baseOffset, prevOffset + 1);
        g.evt.syllableOffset = nextOffset;
        g.evt.anchorType = nextOffset > 0 ? 'subword' : 'word';
        prevOffset = nextOffset;
      });
    });
    // Collapse exact same-time duplicates on the same line regardless of anchor.
    // These are almost always alignment artifacts and create visible repeated labels.
    const dedupedTimed = [];
    const seenExact = new Set();
    normalizedTimed.forEach((evt) => {
      const t = Number(evt.time);
      const timeMs = Number.isFinite(t) ? Math.round(t * 1000) : -1;
      const key = [
        String(evt.chord || ''),
        timeMs,
      ].join('|');
      if (seenExact.has(key)) {
        diagnostics.collapsedExactTimeDuplicateCount += 1;
        return;
      }
      seenExact.add(key);
      dedupedTimed.push(evt);
    });
    normalizedTimed.length = 0;
    normalizedTimed.push(...dedupedTimed);
    // Collapse repeated same-label pre-word spacer chords (e.g. C# C# C# before first lyric word).
    // Keep the last event in each same-label run, since it is closest to lyric onset.
    const preWordCollapsed = [];
    normalizedTimed.forEach((evt) => {
      const isPreWordSpacer = Boolean(evt.isSpacer) && Number(evt.wordIndex) < 0;
      const prev = preWordCollapsed[preWordCollapsed.length - 1];
      if (
        isPreWordSpacer &&
        prev &&
        Boolean(prev.isSpacer) &&
        Number(prev.wordIndex) < 0 &&
        String(prev.chord || '') === String(evt.chord || '')
      ) {
        preWordCollapsed[preWordCollapsed.length - 1] = evt;
        diagnostics.collapsedNearDuplicateCount += 1;
        return;
      }
      preWordCollapsed.push(evt);
    });
    normalizedTimed.length = 0;
    normalizedTimed.push(...preWordCollapsed);

    diagnostics.perLineAnchorPlacements[li] = normalizedTimed.map((e) => ({
      chord: e.chord,
      time: e.time,
      wordIndex: e.wordIndex,
      syllableOffset: e.syllableOffset || 0,
      isSpacer: !!e.isSpacer,
      anchorType: e.anchorType || (e.isSpacer ? 'spacer' : ((e.syllableOffset || 0) > 0 ? 'subword' : 'word')),
      anchorRule: e.anchorRule || 'existing',
      anchorMethod: e.anchorMethod || (String(e.anchorRule || '').includes('phoneme') ? 'phoneme' : (String(e.anchorRule || '').includes('vowel') ? 'vowel' : 'linear')),
      anchorProgress: e.anchorProgress != null ? e.anchorProgress : null,
      phonemeSpan: e.phonemeSpan != null ? e.phonemeSpan : null,
    }));

    [...normalizedTimed, ...untimed].forEach((e) => {
      const c = { ...e };
      delete c._idx;
      out.push(c);
    });
  });

  return { alignment: out, diagnostics };
}
